import isEmpty from 'lodash/isEmpty';
import remove from 'lodash/remove';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import safeGet from 'lodash/get';

import { LPG_FOR_BUSINESS, LPG_REQUEST_A_CALLBACK_FOR_HOME } from '../constants/page-urls';
import PLAN_NAMES from '../constants/plan-names';
import { WA } from '../constants/regions';
import { IMainState } from './compare-plans';
import { IComparisonPlan } from '@origin-digital/plans';

/**
 * Defines the URL for where a user should go if they are interested in gas.
 *
 * @returns {string} - A URL
 */
export function getLPGCrossSellLink(isBusiness) {
  if (isBusiness) {
    return LPG_FOR_BUSINESS;
  }
  return LPG_REQUEST_A_CALLBACK_FOR_HOME;
}

export function addLoginHashToLocation(event) {
  event.preventDefault();
  window.location.hash = 'login';
}

export function areFilterValuesBasicallyTheSame(filters1, filters2) {
  // Quickly compare the filter states, to see if any of the following have changed
  // we don't care what has changed, only that they did. So in order to do this quickly,
  // we stringify the objects, and if the strings are different there's changes.
  // The catch is, there's part of the object we need to ignore.

  const filters1Subset = {
    servicesRequired: {
      electricity: filters1.requiresElectricity,
      gas: filters1.requiresGas,
    },
    // hasSolarPanels: filters1.hasSolarPanels,
    renewableEnergy: filters1.renewableEnergy,
    // isCustomerMoving: filters1.isCustomerMoving,
    // postcode: filters1.postcode
  };
  const filters2Subset = {
    servicesRequired: {
      electricity: filters2.requiresElectricity,
      gas: filters2.requiresGas,
    },
    // hasSolarPanels: filters2.hasSolarPanels,
    renewableEnergy: filters2.renewableEnergy,
    // isCustomerMoving: filters2.isCustomerMoving,
    // postcode: filters2.postcode
  };

  return JSON.stringify(filters1Subset) === JSON.stringify(filters2Subset);
}

/**
 * Once the user has finished interacting with the Compare Plans app, their next
 * destination is Step 1 of the Sales Form. Depending on whether the user is
 * resi/busi, we navigate them to the correct page.
 *
 * This assumes that all the data in ContextHub is already set to the correct
 * values and the browser is ready to navigate onwards.
 *
 * @param {{ business: string, residential: string}} getStartedUrls
 * @param {{ isBusiness?: boolean, isResidential?: boolean}} userDetails
 */
export function finish(getStartedUrls, userDetails) {
  if (userDetails.isBusiness) {
    goToUrl(getStartedUrls.business);
  } else if (userDetails.isResidential) {
    goToUrl(getStartedUrls.residential);
  } else {
    throw new RangeError('Unknown site universe; cannot continue');
  }
}

function goToUrl(url) {
  global.location.assign(url);
}

/**
 * The plans need to be sorted by an order which is returned from AEM. To find the correct order to use we need
 * to concatenate the users region and connection (e.g. VIC-PROS_SWT) to find the correct key in the orders object.
 * If no sort order exists for that type of user we return the first 3 plans.
 *
 * The plans should then be sorted by comparing the description attribute of each plan against the order. The first
 * N plans are only returned.
 * @param plans - unsorted array of plans
 * @param userRegion - the region of the user
 * @param filters - filters settings
 * @param planOrder - sort order of plans
 */
export function sortPlans(plans, userRegion, filters, planOrder) {
  let plansSorted = plans;

  if (!isEmpty(planOrder)) {
    const planKey = `${userRegion}-${filters.connectionScenario}`;
    const order = planOrder[planKey];

    if (typeof order !== 'undefined') {
      const orderedPlanNames = Object.values(order);
      plansSorted = doSort(plansSorted, orderedPlanNames);
    }
  }

  return plansSorted;
}

export const personalisePlans = (plans, personalisedPlanData) => {
  const personalisedOrder = safeGet(personalisedPlanData, 'order');
  const personalisedPills = safeGet(personalisedPlanData, 'pills');
  let sortedPlans = plans.slice();

  if (!isEmpty(personalisedOrder)) {
    sortedPlans = doSort(sortedPlans, personalisedOrder);
  }

  if (!isEmpty(personalisedPills)) {
    sortedPlans = sortedPlans.map((plan) => {
      const matchingPillText = personalisedPills[plan.description];
      if (matchingPillText !== undefined) {
        plan.chargesDiscountCopy = matchingPillText;
      }
      return plan;
    });
  }

  return sortedPlans;
};

/**
 * Pass in an array of plan identifier to exclude them from the plan list
 *
 * @param plans
 * @param plansToExclude - Array of plans
 */
export function excludePlans(plans: IComparisonPlan[], plansToExclude = []) {
  return plans.filter((plan) => !plansToExclude.includes(plan.identifier));
}

/**
 * Sorting logic for plans. Sort the plans by the sort order provided by AEM.
 *
 * If none of the plans are found in the sort order then just return the plans as is.
 *
 * If a plan in the sort order cannot be found in the plans array then skip the plan and return the next best
 * plan in the 3rd position.
 * @param plans - unsorted plans array
 * @param order - sort order of plans
 */
export function doSort(plans, order) {
  return plans.sort((planA, planB) => {
    const planAindex = order.indexOf(planA.description);
    const planBindex = order.indexOf(planB.description);

    if (planAindex === planBindex) {
      return 0;
    } else if (planAindex < planBindex) {
      if (planAindex === -1) {
        return 1;
      } else {
        return -1;
      }
    } else if (planAindex > planBindex) {
      if (planBindex === -1) {
        return -1;
      } else {
        return 1;
      }
    }
  });
}

/**
 * If there is a "Solar Boost" plan, we need to figure out where to show it
 * array
 * @param plansSorted - sorted array of plans (max length 3)
 * @param maxPlans - number of plan to return
 */
export function doSolarShuffle(plansSorted, maxPlans = 3) {
  const plans = plansSorted.slice();
  const solarPlans = remove(plans, (p: any) => p.description === PLAN_NAMES.SOLAR_BOOST);

  const SOLAR_REPLACEABLE_PLANS = [
    PLAN_NAMES.ORIGIN_SUPPLY,
    PLAN_NAMES.ORIGIN_BASIC,
    PLAN_NAMES.HOME_SUPPORT,
  ];
  const replaceableIndex = findIndex(
    plans,
    (p: any) => SOLAR_REPLACEABLE_PLANS.indexOf(p.description) > -1,
  );

  if (solarPlans.length > 0) {
    if (plans.length < maxPlans) {
      plans.push(solarPlans[0]);
    } else if (replaceableIndex > -1 && replaceableIndex < maxPlans) {
      plans[replaceableIndex] = solarPlans[0];
    } else {
      plans[maxPlans - 1] = solarPlans[0];
    }
  }

  return plans.slice(0, maxPlans);
}

/**
 * Concat the title of each plan into a comma separated string for analytics tracking. Save the result into a object
 * with field name 'comparePlansProducts'
 * @param plans
 * @returns {{comparePlansProducts: string}}
 */
export function getTrackingPlansNames(plans) {
  let trackingString = '';
  for (let plan of plans) {
    trackingString += `${plan.title},`;
  }
  return { comparePlansProducts: trackingString };
}

/**
 * @param {{user: {userRegion: string? }}} state
 * @returns {boolean}
 */
export function shouldGeoTarget(state, sessionPersistence) {
  return !sessionPersistence.getItem('plansearchpostcode') && !state.user.userRegion;
}

export function getPPEligibilityByState(state) {
  let notEligibleStates = new Set();
  notEligibleStates.add(WA.id);

  return !notEligibleStates.has(state);
}

/**
 * Checks to see if sapAddressId exists in the state
 * (i.e. whether the user has done an address look up).
 * If so return a query param string to be appended to the sales form url.
 *
 * @param state
 */
export const getAddressIDQqueryParam = (state: IMainState) => {
  const addressId = safeGet(state, 'qasAddressObject.sapAddressId');
  return addressId ? `?addressId=${addressId}` : '';
};

/**
 * Special exception case, we want to use content for Origin Everyday Rewards for Origin Go plan.
 *
 * @param planTitle
 */
export const getContentSourceTitle = (planTitle: string) => {
  if (planTitle && planTitle.toLowerCase() === 'origin go') {
    return 'Origin Everyday Rewards';
  }
  return planTitle;
};


/* Ensure we only show 2 plans for VIC before applying solar or recommended plan */
export const doVicCulling = (plans) => {
  const { ORIGIN_BASIC, HOME_SUPPORT, SOLAR_BOOST } = PLAN_NAMES;

  const BASIC_PLANS = [ORIGIN_BASIC, HOME_SUPPORT];

  const vicPlans = plans.slice();
  const basicPlans = remove(vicPlans, (plan: any) => BASIC_PLANS.indexOf(plan.description) > -1);
  const solarPlans = remove(vicPlans, (plan: any) => plan.description === SOLAR_BOOST);

  return [
    ...vicPlans.slice(0, 1),
    ...basicPlans.slice(0, 1),
    ...solarPlans.slice(0, 1)
  ];
};

export const injectRecommendedPlan = (recommendedPlan, plansSorted, allPlans, numberOfPlans) => {
  const upToDateRecommendedPlan = find(allPlans, {
    description: recommendedPlan.description,
  });

  if (upToDateRecommendedPlan) {
    const plansSortedWithoutRecommended = plansSorted.filter(
      (p) => p.description !== recommendedPlan.description,
    );
    
    if (plansSortedWithoutRecommended.length >= numberOfPlans) {
      plansSortedWithoutRecommended.splice(1, 1);
    }

    return [
      upToDateRecommendedPlan,
      ...plansSortedWithoutRecommended,
    ].slice(0, numberOfPlans);
  }

  return plansSorted.slice(0, numberOfPlans);
};
